import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAngelBySlug } from '../utils/angelsData';

const AngelDetails = () => {
    const { angelName } = useParams(); // Now using angelName instead of id
    const navigate = useNavigate();
    const angel = getAngelBySlug(angelName);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // If angel not found
    if (!angel) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl text-white mb-4">Angel Not Found</h1>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg"
                    >
                        Return Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className="min-h-screen bg-black text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
        >
            {/* Background Gradient */}
            <div className={`absolute inset-0 opacity-10 pointer-events-none`} />

            {/* Navigation */}
            <motion.div
                className="relative z-10 p-6"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
            >
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center text-gray-400 hover:text-white transition-colors duration-300"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Angels
                </button>
            </motion.div>

            <div className="relative z-10 max-w-6xl mx-auto px-4 py-8">
                {/* Hero Section */}
                <motion.div
                    className="grid md:grid-cols-2 gap-12 items-start mb-16"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {/* Image */}
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <img
                            src={angel.image}
                            alt={angel.name}
                            className="w-full h-full object-cover rounded-2xl border border-gray-800 shadow-2xl"
                        />
                    </motion.div>

                    {/* Basic Info */}
                    <div className='flex h-full justify-center  flex-col'>
                        <motion.h1
                            className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.6 }}
                        >
                            {angel.name}
                        </motion.h1>

                        <motion.h2
                            className="text-2xl md:text-3xl text-gray-300 mb-6"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.7 }}
                        >
                            {angel.title}
                        </motion.h2>

                        <motion.p
                            className="text-xl text-gray-400 italic mb-6 leading-relaxed"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            "{angel.tagline}"
                        </motion.p>

                        <motion.div
                            className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border-l-4 border-red-500 p-4 rounded-r-lg"
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.9 }}
                        >
                            <p className="text-lg text-gray-200">
                                {angel.quickLogline}
                            </p>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Rest of the component remains the same */}
                {/* Profile Snapshot */}
                <motion.section
                    className="mb-12"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.0 }}
                >
                    <h3 className="text-3xl font-bold mb-6 text-gray-200 border-b border-gray-800 pb-2">
                        Profile Snapshot
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <span className="text-gray-400">Real Name:</span>
                                <p className="text-white text-lg">{angel.profile.realName}</p>
                            </div>
                            <div>
                                <span className="text-gray-400">Base:</span>
                                <p className="text-white text-lg">{angel.profile.base}</p>
                            </div>
                            <div>
                                <span className="text-gray-400">Role:</span>
                                <p className="text-white text-lg">{angel.profile.role}</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <span className="text-gray-400">Vibe:</span>
                                <p className="text-white text-lg italic">{angel.profile.vibe}</p>
                            </div>
                            <div>
                                <span className="text-gray-400">First Feature:</span>
                                <p className="text-white text-lg">{angel.profile.firstFeature}</p>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* Powers & Abilities */}
                <motion.section
                    className="mb-12"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.1 }}
                >
                    <h3 className="text-3xl font-bold mb-6 text-gray-200 border-b border-gray-800 pb-2">
                        Powers & Abilities
                    </h3>
                    <ul className="grid md:grid-cols-2 gap-4">
                        {angel.powers.map((power, index) => (
                            <motion.li
                                key={index}
                                className="flex items-start space-x-3 bg-gray-900/50 p-4 rounded-lg border border-gray-800"
                                whileHover={{ scale: 1.02 }}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 1.2 + index * 0.1 }}
                            >
                                <span className="text-red-500 mt-1">•</span>
                                <span className="text-gray-300">{power}</span>
                            </motion.li>
                        ))}
                    </ul>
                </motion.section>

                {/* Origin & Backstory */}
                <motion.section
                    className="mb-12"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.3 }}
                >
                    <h3 className="text-3xl font-bold mb-6 text-gray-200 border-b border-gray-800 pb-2">
                        Origin & Backstory
                    </h3>
                    <p className="text-gray-300 text-lg leading-relaxed">
                        {angel.origin}
                    </p>
                </motion.section>

                {/* Personality */}
                <motion.section
                    className="mb-12"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.4 }}
                >
                    <h3 className="text-3xl font-bold mb-6 text-gray-200 border-b border-gray-800 pb-2">
                        Personality
                    </h3>
                    <p className="text-gray-300 text-lg leading-relaxed">
                        {angel.personality}
                    </p>
                </motion.section>

                {/* Role in The Angels Universe */}
                <motion.section
                    className="mb-12"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.5 }}
                >
                    <h3 className="text-3xl font-bold mb-6 text-gray-200 border-b border-gray-800 pb-2">
                        Role in The Angels Universe
                    </h3>
                    <p className="text-gray-300 text-lg leading-relaxed">
                        {angel.role}
                    </p>
                </motion.section>
            </div>
        </motion.div>
    );
};

export default AngelDetails;